import React, { useState, useEffect } from "react";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import { motion } from "framer-motion";

const API_URL = `${process.env.REACT_APP_API_URL}/admin/gifts`;
const DISCOUNT_API_SET = `${process.env.REACT_APP_API_URL}/gifts/discount/set`;
const DISCOUNT_API_REMOVE = `${process.env.REACT_APP_API_URL}/gifts/discount/remove`;
const OCCASION_API = `${process.env.REACT_APP_API_URL}/admin/gifts/occasion`;

export default function GiftAdminPage() {
  const [gifts, setGifts] = useState([]);
  const [form, setForm] = useState({
    name: "",
    slug: "",
    description: "",
    basePrice: "",
    imageUrl: "",
    tags: "",
    isActive: true,
  });
  const [editingId, setEditingId] = useState(null);

  // Occasion management
  const [occasions, setOccasions] = useState([]);
  const [newOccasion, setNewOccasion] = useState("");

  // 🔍 Filters
  const [search, setSearch] = useState("");
  const [filterTag, setFilterTag] = useState("All");

  // Discount controls
  const [discountTag, setDiscountTag] = useState("");
  const [discountPercent, setDiscountPercent] = useState("");

  // Fetch all gifts
  const fetchGifts = async () => {
    try {
      const { data } = await axios.get(API_URL);
      setGifts(data);
    } catch (err) {
      toast.error("Failed to load gifts");
    }
  };

  // Fetch all occasions
  const fetchOccasions = async () => {
    try {
      const { data } = await axios.get(`${OCCASION_API}`);
      setOccasions(data);
    } catch (err) {
      toast.error("Failed to load occasions");
    }
  };

  useEffect(() => {
    fetchGifts();
    fetchOccasions();
  }, []);

  // Add new occasion
  const handleAddOccasion = async () => {
    if (!newOccasion.trim()) return;
    try {
      await axios.post(`${OCCASION_API}`, { name: newOccasion });
      toast.success("Occasion added");
      setNewOccasion("");
      fetchOccasions();
    } catch (err) {
      toast.error("Failed to add occasion");
    }
  };

  // Delete occasion
  const handleDeleteOccasion = async (id) => {
    if (!window.confirm("Delete this occasion?")) return;
    try {
      await axios.delete(`${OCCASION_API}/${id}`);
      toast.success("Occasion deleted");
      fetchOccasions();
    } catch (err) {
      toast.error("Failed to delete occasion");
    }
  };

  // Update gift's occasions/tags
  const handleUpdateGiftOccasion = async (giftId, tags) => {
    try {
      await axios.put(`${API_URL}/${giftId}/occasion`, { tags });
      toast.success("Gift occasions/tags updated");
      fetchGifts();
    } catch (err) {
      toast.error("Failed to update gift occasions/tags");
    }
  };

  // Handle form input
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  // Add / Update Gift
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API_URL}/${editingId}`, form);
        toast.success("Gift updated successfully 🎉");
      } else {
        await axios.post(API_URL, form);
        toast.success("Gift added successfully 🎁");
      }
      setForm({ name: "", slug: "", description: "", basePrice: "", imageUrl: "", tags: "", isActive: true });
      setEditingId(null);
      fetchGifts();
    } catch (error) {
      toast.error("Error saving gift ❌");
    }
  };

  // Edit gift
  const handleEdit = (gift) => {
    setForm(gift);
    setEditingId(gift.id);
    toast("Editing mode enabled ✏️");
  };

  // Delete gift
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this gift?")) {
      try {
        await axios.delete(`${API_URL}/${id}`);
        fetchGifts();
        toast.success("Gift deleted successfully 🗑️");
      } catch {
        toast.error("Error deleting gift ❌");
      }
    }
  };

  // Filtered Gifts
  const uniqueTags = ["All", ...new Set(gifts.flatMap((g) => g.tags?.split(",") || []))];
  const filteredGifts = gifts.filter((g) => {
    const matchesSearch = g.name.toLowerCase().includes(search.toLowerCase());
    const matchesTag = filterTag === "All" || g.tags?.split(",").map((t) => t.trim()).includes(filterTag);
    return matchesSearch && matchesTag;
  });

  // Discount Feature
  const handleSetDiscount = async () => {
    if (!discountTag || !discountPercent) {
      toast.error("Please enter both tag and discount percent");
      return;
    }
    try {
      await axios.post(DISCOUNT_API_SET, {
        occasion: discountTag,
        discountPercent: Number(discountPercent),
      });
      toast.success(`Discount set for "${discountTag}" gifts`);
      setDiscountTag("");
      setDiscountPercent("");
      fetchGifts();
    } catch (err) {
      toast.error("Failed to set discount");
    }
  };

  const handleRemoveDiscount = async () => {
    if (!discountTag) {
      toast.error("Please enter a tag to remove discount");
      return;
    }
    try {
      await axios.post(DISCOUNT_API_REMOVE, {
        occasion: discountTag,
      });
      toast.success(`Discount removed for "${discountTag}" gifts`);
      setDiscountTag("");
      setDiscountPercent("");
      fetchGifts();
    } catch (err) {
      toast.error("Failed to remove discount");
    }
  };

  return (
    <div className="container py-4 bg-light min-vh-100">
      <Toaster position="top-right" />

      <h1 className="text-center fw-bold text-success mb-4">🎁 Gift Products Admin</h1>

      {/* --- Occasion Management --- */}
      <div className="bg-white shadow rounded p-3 mb-4 border">
        <h5 className="mb-3">Manage Occasions/Tags</h5>
        <div className="row g-2 align-items-center">
          <div className="col-md-4">
            <input
              type="text"
              placeholder="New Occasion (e.g. birthday)"
              value={newOccasion}
              onChange={e => setNewOccasion(e.target.value)}
              className="form-control"
            />
          </div>
          <div className="col-md-2">
            <button className="btn btn-primary" onClick={handleAddOccasion}>
              Add Occasion
            </button>
          </div>
          <div className="col-md-6">
            <div className="d-flex flex-wrap gap-2">
              {occasions.map(o => (
                <span key={o.id} className="badge bg-info text-dark px-3 py-2">
                  {o.name}
                  <button
                    className="btn btn-sm btn-danger ms-2"
                    style={{ padding: "0 6px" }}
                    onClick={() => handleDeleteOccasion(o.id)}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* --- Discount Controls --- */}
      <div className="bg-white shadow rounded p-3 mb-4 border">
        <h5 className="mb-3">Set/Remove Discount by Occasion Tag</h5>
        <div className="row g-2 align-items-center">
          <div className="col-md-4">
            <input
              type="text"
              placeholder="Occasion Tag (e.g. birthday)"
              value={discountTag}
              onChange={e => setDiscountTag(e.target.value)}
              className="form-control"
            />
          </div>
          <div className="col-md-3">
            <input
              type="number"
              min="0"
              max="100"
              placeholder="Discount %"
              value={discountPercent}
              onChange={e => setDiscountPercent(e.target.value)}
              className="form-control"
            />
          </div>
          <div className="col-md-5 d-flex gap-2">
            <button className="btn btn-success" onClick={handleSetDiscount}>
              Set Discount
            </button>
            <button className="btn btn-danger" onClick={handleRemoveDiscount}>
              Remove Discount
            </button>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white shadow rounded p-4 mb-4 border">
        <div className="row g-3">
          <div className="col-md-6">
            <input
              name="name"
              placeholder="Name"
              value={form.name}
              onChange={handleChange}
              className="form-control"
              required
            />
          </div>
          <div className="col-md-6">
            <input
              name="slug"
              placeholder="Slug"
              value={form.slug}
              onChange={handleChange}
              className="form-control"
              required
            />
          </div>
          <div className="col-md-6">
            <input
              name="basePrice"
              placeholder="Price"
              type="number"
              step="0.01"
              value={form.basePrice}
              onChange={handleChange}
              className="form-control"
              required
            />
          </div>
          <div className="col-md-6">
            <input
              name="imageUrl"
              placeholder="Image URL"
              value={form.imageUrl}
              onChange={handleChange}
              className="form-control"
            />
          </div>
          <div className="col-md-6">
            <input
              name="tags"
              placeholder="Tags (birthday,pooja,...)"
              value={form.tags}
              onChange={handleChange}
              className="form-control"
            />
          </div>
          <div className="col-md-6 d-flex align-items-center">
            <input
              type="checkbox"
              name="isActive"
              checked={form.isActive}
              onChange={handleChange}
              className="form-check-input me-2"
            />
            <label className="form-check-label">Active</label>
          </div>
        </div>

        <textarea
          name="description"
          placeholder="Description"
          value={form.description}
          onChange={handleChange}
          className="form-control mt-3"
        />

        <button type="submit" className="btn btn-success mt-3">
          {editingId ? "Update Gift ✨" : "Add Gift ➕"}
        </button>
      </form>

      {/* 🔍 Search & Filter */}
      <div className="row g-3 mb-3">
        <div className="col-md-8">
          <input
            type="text"
            placeholder="🔍 Search by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="form-control"
          />
        </div>
        <div className="col-md-4">
          <select
            value={filterTag}
            onChange={(e) => setFilterTag(e.target.value)}
            className="form-select"
          >
            {uniqueTags.map((tag, idx) => (
              <option key={idx} value={tag}>
                {tag}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Gift List */}
      <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
        {filteredGifts.length === 0 ? (
          <p className="text-center text-muted mt-4">No gifts found ❌</p>
        ) : (
          <div className="row g-4">
            {filteredGifts.map((gift) => (
              <motion.div
                key={gift.id}
                className="col-md-4"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                whileHover={{ scale: 1.03 }}
              >
                <div className="card shadow-sm h-100">
                  {gift.imageUrl && (
                    <img
                      src={gift.imageUrl}
                      alt={gift.name}
                      className="card-img-top"
                      style={{ height: "200px", objectFit: "cover" }}
                    />
                  )}
                  <div className="card-body d-flex flex-column">
                    <h5 className="card-title fw-bold">{gift.name}</h5>
                    <p className="card-text text-muted">{gift.description}</p>
                    <p className="fw-bold text-success">
                      ₹{gift.discountedPrice ? gift.discountedPrice : gift.basePrice}
                      {gift.discountPercent > 0 && (
                        <span style={{ color: "red", marginLeft: "8px", fontSize: "0.95em" }}>
                          <del>₹{gift.basePrice}</del> ({gift.discountPercent}% OFF)
                        </span>
                      )}
                    </p>
                    <small className="text-muted">Tags: {gift.tags}</small>
                    <div className="mt-auto d-flex justify-content-between pt-3">
                      <button
                        onClick={() => handleEdit(gift)}
                        className="btn btn-warning btn-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(gift.id)}
                        className="btn btn-danger btn-sm"
                      >
                        Delete
                      </button>
                    </div>
                    {/* Occasion/Tag Editor */}
                    <div className="mt-3">
                      <strong>Edit Occasions/Tags:</strong>
                      <input
                        type="text"
                        value={gift.tags || ""}
                        onChange={e => handleUpdateGiftOccasion(gift.id, e.target.value)}
                        className="form-control d-inline-block w-75 mt-1"
                        placeholder="Comma separated tags"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}